import React, { useEffect, useMemo, useState } from 'react';

import { Badge } from '@buildoutinc/blueprint-react/ui/Badge';
import { Tab, Tabs, TabsList } from '@buildoutinc/blueprint-react/ui/Tabs';
import debounce from 'lodash/debounce';
import moment from 'moment';

import { ISO_DATE_FORMAT } from 'helpers/formatters/date_time_formats';
import { getTaskCounts } from '../helpers';
import { useTasks } from '../context/tasks_context';

import type { Filters } from '../filters/types';
import type { TaskCounts } from '../helpers';

type QuickFilterKey = 'open' | 'overdue' | 'today' | 'week' | 'noDate' | 'completed';

interface QuickFilterOption {
  key: QuickFilterKey;
  label: string;
  countKey: keyof TaskCounts;
}

const QUICK_FILTER_OPTIONS: QuickFilterOption[] = [
  { key: 'open', label: 'Open Tasks', countKey: 'incomplete' },
  { key: 'overdue', label: 'Overdue', countKey: 'overdue' },
  { key: 'today', label: 'Today', countKey: 'today' },
  { key: 'week', label: 'This week', countKey: 'week' },
  { key: 'noDate', label: 'No date', countKey: 'noDate' },
  { key: 'completed', label: 'Completed', countKey: 'completed' },
];

function weekEnd(): string {
  return moment().add(7, 'days').format(ISO_DATE_FORMAT);
}

function filterChangesFor(key: QuickFilterKey): Partial<Filters> {
  const today = moment().format(ISO_DATE_FORMAT);
  const base: Partial<Filters> = {
    completed: false,
    open: false,
    overdue: false,
    fromDate: null,
    toDate: null,
    tbd: null,
  };

  switch (key) {
  case 'open':
    return { ...base, open: true, overdue: true };
  case 'overdue':
    return { ...base, overdue: true };
  case 'today':
    return { ...base, fromDate: today, toDate: today };
  case 'week':
    return { ...base, fromDate: today, toDate: weekEnd() };
  case 'noDate':
    return { ...base, tbd: true };
  case 'completed':
    return { ...base, completed: true };
  }
}

function activeQuickFilter({ completed, open, overdue, fromDate, toDate, tbd }: Filters): QuickFilterKey | null {
  const today = moment().format(ISO_DATE_FORMAT);

  if (tbd) return 'noDate';
  if (fromDate === today && toDate === today) return 'today';
  if (fromDate === today && toDate === weekEnd()) return 'week';
  if (open) return 'open';
  if (overdue) return 'overdue';
  if (completed) return 'completed';

  return null;
}

function formatCount(value: number | undefined): string | null {
  if (value === undefined) return null;
  if (value > 99) return '99+';
  return String(value);
}

interface CountBadgeProps {
  counts: TaskCounts | null;
  countsLoading: boolean;
  countKey: keyof TaskCounts;
}

function CountBadge({ counts, countsLoading, countKey }: CountBadgeProps) {
  if (!counts && !countsLoading) return null;
  const label = counts ? formatCount(counts[countKey]) : null;

  return (
    <Badge className="rounded-2" style={{ height: 14, minWidth: 14 }} variant="secondary">
      {countsLoading && !counts ? '…' : label}
    </Badge>
  );
}

export function TasksQuickFilters() {
  const { filters, changeFilters, paths } = useTasks();
  const [counts, setCounts] = useState<TaskCounts | null>(null);
  const [countsLoading, setCountsLoading] = useState(false);

  const countsPath = paths.counts;

  const debouncedLoadCounts = useMemo(
    () => debounce(async (filters: Filters) => {
      if (!countsPath) return;

      setCountsLoading(true);
      try {
        const data = await getTaskCounts({
          path: countsPath,
          filters: {
            allTasks: filters.allTasks,
            assigneeId: filters.assigneeId,
            officeId: filters.officeId,
            parentTypeFilter: filters.parentType,
            query: filters.query,
            tbd: filters.tbd,
            type: filters.type,
          },
        });
        if (data) {
          setCounts(data);
        }
      } finally {
        setCountsLoading(false);
      }
    }, 500),
    [countsPath]
  );

  useEffect(() => {
    if (!countsPath) return;
    debouncedLoadCounts(filters);
  }, [
    countsPath,
    debouncedLoadCounts,
    filters.allTasks,
    filters.assigneeId,
    filters.officeId,
    filters.parentType,
    filters.query,
    filters.tbd,
    filters.type,
  ]);

  const activeKey = useMemo(() => activeQuickFilter(filters), [filters]);

  function selectQuickFilter(key: QuickFilterKey) {
    if (activeKey === key) return;
    changeFilters(filterChangesFor(key));
  }

  return (
    <Tabs orientation="horizontal" value={activeKey}>
      <TabsList className="p-1" variant="pills">
        {QUICK_FILTER_OPTIONS.map(option => (
          <Tab
            key={option.key}
            value={option.key}
            onClick={() => selectQuickFilter(option.key)}
          >
            <span className="d-flex align-items-center gap-1">
              {option.label}
              <CountBadge countKey={option.countKey} counts={counts} countsLoading={countsLoading} />
            </span>
          </Tab>
        ))}
      </TabsList>
    </Tabs>
  );
}
